import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Text, Layer } from 'grommet';
import { useAppTheme } from '@Components/Context';
import {
  Seo, InfoNotice, AppButton, FlexibleSelectDropdown, DatetimeCalendar,
} from '@Components/Control';
import { AdminNavWrapper } from '@Components/Navigation';
import { buildDateTimeLabel } from '@Helpers/util';
import {
  fetchAdminYtAudits,
  fetchAdminYtAuditDetail,
  fetchAdminYtAuditUsers,
  batchCreateAdminAudits,
  removeAdminYtAuditChannel,
  adminYtAuditDetailClear,
  adminYtAuditsClear,
} from '@Actions';
import { fetchAuditPdf } from '@Actions/creatorSatellite/auditActions';
import {
  TableWrapper,
  NoDataPlaceholder,
  tablePadding,
  renderTableHeader,
  SearchInput,
} from '../../../Partial/Product/SharedComponents';
import { fmt, StatusBadge } from './YtAuditsShared';
import AdminYtAuditsCreateModal from './AdminYtAuditsCreateModal';
import AdminYtAuditDetailModal from './AdminYtAuditDetailModal';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { api: 'pending', display: 'Pending' },
  { api: 'running', display: 'Running' },
  { api: 'completed', display: 'Completed' },
  { api: 'failed', display: 'Failed' },
];

const HEADER = [
  { display: '#', uid: 'index', size: '4%' },
  { display: 'Channel', uid: 'channel', size: '20%' },
  { display: 'Email', uid: 'email', size: '15%' },
  { display: 'Admin User', uid: 'adminUser', size: '13%' },
  { display: 'Status', uid: 'status', size: '11%' },
  { display: 'Monthly Recovery', uid: 'revenue', size: '11%' },
  { display: 'Created', uid: 'createdAt', size: '10%' },
  { display: '', uid: 'actions', size: '16%' },
];

function ProgressBar({ value }) {
  return (
    <Box direction="row" align="center" gap="0.4rem" margin={{ top: '0.3rem' }}>
      <Box
        background="var(--page-table-border)"
        round="2px"
        style={{ width: '64px', height: '4px', overflow: 'hidden' }}
      >
        <Box
          background="#3b82f6"
          round="2px"
          style={{ width: `${value || 0}%`, height: '100%' }}
        />
      </Box>
      <Text size="0.72rem" color="var(--page-secondary-text)">
        {value ?? 0}
        %
      </Text>
    </Box>
  );
}

ProgressBar.propTypes = { value: PropTypes.number };
ProgressBar.defaultProps = { value: 0 };

const AdminYtAuditsPage = ({
  small,
  location,
  loading,
  audits = null,
  total = 0,
  detail = null,
  adminUsers = null,
  fetchAudits,
  fetchDetail,
  fetchUsers,
  batchCreate,
  removeChannel,
  clearDetail,
  clearAudits,
}) => {
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const usePageBg = isDarkMode ? page.background : page.altBg;

  const [isAdmin, setIsAdmin] = React.useState(true);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [statusFilter, setStatusFilter] = React.useState('');
  const [adminUserFilter, setAdminUserFilter] = React.useState('');
  const [searchInput, setSearchInput] = React.useState('');
  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');

  const [copiedId, setCopiedId] = React.useState(null);

  const [showCreate, setShowCreate] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [showDetail, setShowDetail] = React.useState(false);
  const [showDatePicker, setShowDatePicker] = React.useState(false);
  const [pendingDateFrom, setPendingDateFrom] = React.useState('');
  const [pendingDateTo, setPendingDateTo] = React.useState('');

  const pollRef = React.useRef(null);

  const queryParams = React.useMemo(() => {
    const p = { page: currentPage, limit: PAGE_SIZE };
    if (statusFilter) p.status = statusFilter;
    /* eslint-disable camelcase */
    if (adminUserFilter) p.admin_user_id = adminUserFilter;
    if (searchInput) p.search = searchInput;
    if (dateFrom) p.start_date = dateFrom;
    if (dateTo) p.end_date = dateTo;
    p.is_admin = isAdmin ? 'true' : 'false';
    /* eslint-enable camelcase */
    return p;
  }, [currentPage, statusFilter, adminUserFilter, searchInput, dateFrom, dateTo, isAdmin]);

  React.useEffect(() => {
    setCurrentPage(0);
  }, [statusFilter, adminUserFilter, searchInput, dateFrom, dateTo, isAdmin]);

  React.useEffect(() => {
    fetchAudits(queryParams);
    return () => { clearAudits(); };
  }, [queryParams]);

  React.useEffect(() => {
    fetchUsers();
  }, []);

  React.useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    const hasPending = Array.isArray(audits) && audits.some(
      (a) => a.status === 'pending' || a.status === 'running',
    );
    if (hasPending) {
      pollRef.current = setInterval(() => fetchAudits(queryParams), 15000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [audits, queryParams]);

  const handleToggleIsAdmin = React.useCallback((value) => {
    setIsAdmin(value);
    setAdminUserFilter('');
    setCurrentPage(0);
  }, []);

  const handleReset = React.useCallback(() => {
    setStatusFilter('');
    setAdminUserFilter('');
    setSearchInput('');
    setDateFrom('');
    setDateTo('');
    setPendingDateFrom('');
    setPendingDateTo('');
    setCurrentPage(0);
  }, []);

  const handleViewDetail = React.useCallback((audit) => {
    fetchDetail(audit.auditId);
    setShowDetail(true);
  }, [fetchDetail]);

  const handleCloseDetail = React.useCallback(() => {
    setShowDetail(false);
    clearDetail();
  }, [clearDetail]);

  const handleBatchCreate = React.useCallback(async (channels) => {
    setSubmitting(true);
    await batchCreate(channels, () => {
      setShowCreate(false);
      setCurrentPage(0);
      fetchAudits({ page: 0, limit: PAGE_SIZE });
    });
    setSubmitting(false);
  }, [batchCreate, fetchAudits]);

  const handleSearch = React.useCallback((_, value) => {
    setSearchInput(value || '');
  }, []);

  const handleDateRangeSelect = React.useCallback((from, to) => {
    setPendingDateFrom(from || '');
    setPendingDateTo(to || '');
  }, []);

  const handleCloseDatePicker = React.useCallback(() => {
    setDateFrom(pendingDateFrom);
    setDateTo(pendingDateTo);
    setShowDatePicker(false);
  }, [pendingDateFrom, pendingDateTo]);

  const handleOpenDatePicker = React.useCallback(() => {
    setPendingDateFrom(dateFrom);
    setPendingDateTo(dateTo);
    setShowDatePicker(true);
  }, [dateFrom, dateTo]);

  const padHorizontal = small ? '1rem' : '2rem';

  const adminUserOptions = React.useMemo(() => (adminUsers || []).map((u) => ({
    api: String(u.id),
    display: u.email,
  })), [adminUsers]);

  const hasActiveFilters = statusFilter || adminUserFilter || searchInput || dateFrom || dateTo;

  const visibleHeader = React.useMemo(
    () => HEADER.filter((col) => isAdmin || col.uid !== 'adminUser'),
    [isAdmin],
  );

  const renderCell = (uid, row, idx) => {
    if (uid === 'index') {
      return (
        <Text size="0.85rem" color={page.tableText}>
          {currentPage * PAGE_SIZE + idx + 1}
        </Text>
      );
    }

    if (uid === 'channel') {
      return (
        <Box direction="column" gap="0.15rem">
          {row.channelName && (
            <Text size="0.9rem" color={page.tableText} weight={500} style={{ wordBreak: 'break-all' }}>
              {row.channelName}
            </Text>
          )}
          {row.channelUrl ? (
            <a
              href={row.channelUrl}
              target="_blank"
              rel="noreferrer"
              style={{ color: '#e53e3e', fontSize: '0.78rem', wordBreak: 'break-all' }}
            >
              {row.channelUrl}
            </a>
          ) : (
            <Text size="0.78rem" color={page.secondaryText}>—</Text>
          )}
        </Box>
      );
    }

    if (uid === 'email') {
      return (
        <Text size="0.85rem" color={page.tableText}>
          {row.email || '—'}
        </Text>
      );
    }

    if (uid === 'adminUser') {
      return (
        <Text size="0.85rem" color={page.tableText}>
          {row.adminUserEmail || '—'}
        </Text>
      );
    }

    if (uid === 'status') {
      return (
        <Box direction="column">
          <StatusBadge status={row.status} />
          {(row.status === 'running') && <ProgressBar value={row.progress} />}
        </Box>
      );
    }

    if (uid === 'revenue') {
      const lo = row.estimatedMonthlyRecoveryLow;
      const hi = row.estimatedMonthlyRecoveryHigh;
      if (lo == null && hi == null) {
        return <Text size="0.85rem" color={page.tableText}>—</Text>;
      }
      return (
        <Box direction="column" gap="0.1rem">
          {hi != null && (
            <Text size="0.85rem" color={page.tableText}>
              {`High: $${fmt(hi)}`}
            </Text>
          )}
          {lo != null && (
            <Text size="0.78rem" color={page.secondaryText}>
              {`Low: $${fmt(lo)}`}
            </Text>
          )}
        </Box>
      );
    }

    if (uid === 'createdAt') {
      return (
        <Text size="0.85rem" color={page.tableText}>
          {row.createdAt ? buildDateTimeLabel(row.createdAt) : '—'}
        </Text>
      );
    }

    if (uid === 'actions') {
      return (
        <Box direction="row" gap="0.5rem" wrap={false}>
          <AppButton
            overrideHover
            level="tableControls"
            label="View Details"
            bgColor={button.primaryBg}
            color={button.primaryText}
            onClick={() => handleViewDetail(row)}
            style={{ whiteSpace: 'nowrap' }}
          />
          {row.status === 'completed' && row.auditId && (
            <AppButton
              overrideHover
              level="tableControls"
              label="PDF Report"
              bgColor={page.highlight}
              color={button.primaryText}
              onClick={() => fetchAuditPdf(row.auditId)}
              style={{ whiteSpace: 'nowrap' }}
            />
          )}
          {!isAdmin && row.auditId && (() => {
            const copied = copiedId === row.auditId;
            return (
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `${window.location.origin}/creator-satellite/audit/${row.auditId}`,
                  );
                  setCopiedId(row.auditId);
                  setTimeout(() => setCopiedId(null), 1800);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '0.3rem 0.5rem',
                  border: `0.5px solid ${copied ? 'var(--color-border-success)' : page.tableBorder}`,
                  borderRadius: 'var(--app-radius)',
                  background: 'transparent',
                  color: copied ? 'var(--color-text-success)' : page.secondaryText,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'border-color 150ms, color 150ms',
                  lineHeight: 1.25,
                }}
              >
                <i
                  className={copied ? 'ti ti-check' : 'ti ti-copy'}
                  aria-hidden="true"
                  style={{ fontSize: '13px', lineHeight: '1' }}
                />
                {copied ? 'Copied!' : 'Copy link'}
              </button>
            );
          })()}
        </Box>
      );
    }

    return null;
  };

  const dateRangeLabel = (() => {
    if (dateFrom && dateTo) return `${dateFrom} – ${dateTo}`;
    if (dateFrom) return `From ${dateFrom}`;
    if (dateTo) return `To ${dateTo}`;
    return 'Date range';
  })();

  const dateRangeValue = React.useMemo(() => ({
    from: pendingDateFrom ? new Date(`${pendingDateFrom}T12:00:00`) : undefined,
    to: pendingDateTo ? new Date(`${pendingDateTo}T12:00:00`) : undefined,
  }), [pendingDateFrom, pendingDateTo]);

  const tableControls = (
    <Box wrap direction="row" gap="0.5rem" align="center">
      <Box
        direction="row"
        align="center"
        round="6px"
        flex={false}
        style={{
          background: page.altBg,
          border: `0.5px solid ${page.tableBorder}`,
          padding: '3px',
          gap: '2px',
        }}
      >
        {[{ label: 'Admin Audits', value: true }, { label: 'Other Audits', value: false }].map(({ label, value }) => {
          const active = isAdmin === value;
          return (
            <Box
              key={label}
              as="button"
              onClick={() => handleToggleIsAdmin(value)}
              style={{
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                padding: '4px 12px',
                background: active ? button.primaryBg : 'transparent',
                color: active ? button.primaryText : page.secondaryText,
                fontSize: '0.78rem',
                fontWeight: active ? 600 : 400,
                whiteSpace: 'nowrap',
                transition: 'background 120ms, color 120ms',
                lineHeight: '1.4',
              }}
            >
              {label}
            </Box>
          );
        })}
      </Box>
      <SearchInput
        key="search"
        customPlaceholder="Search channel, email…"
        small={small}
        handleSearch={handleSearch}
        initialValue={searchInput}
      />
      <Box>
        <FlexibleSelectDropdown
          textColor={page.inputText}
          handleItemSelect={(value) => setStatusFilter(value || '')}
          flexibleOptions={STATUS_OPTIONS}
          noSelectionLabel="All statuses"
          displayKey="display"
          valueKey="api"
          iconPath="images/filter-input-icon.svg"
          customPad="0.2rem 0.3rem"
          selectedOption={STATUS_OPTIONS.find((o) => o.api === statusFilter)?.display || 'All statuses'}
          withReset={!!statusFilter}
        />
      </Box>
      {isAdmin && adminUserOptions.length > 0 && (
        <Box>
          <FlexibleSelectDropdown
            textColor={page.inputText}
            handleItemSelect={(value) => setAdminUserFilter(value || '')}
            flexibleOptions={adminUserOptions}
            noSelectionLabel="All admins"
            displayKey="display"
            valueKey="api"
            iconPath="images/filter-input-icon.svg"
            customPad="0.2rem 0.3rem"
            selectedOption={adminUserOptions.find((o) => o.api === adminUserFilter)?.display || 'All admins'}
            withReset={!!adminUserFilter}
            withSearch
          />
        </Box>
      )}
      <AppButton
        overrideHover
        level="tableControls"
        height="var(--table-controls-height)"
        color={(dateFrom || dateTo) ? button.primaryText : page.inputText}
        bgColor={(dateFrom || dateTo) ? page.highlight : page.inputBg}
        label={dateRangeLabel}
        onClick={handleOpenDatePicker}
        style={{ whiteSpace: 'nowrap', border: `1px solid ${page.inputBorder || page.tableBorder}` }}
      />
      <AppButton
        overrideHover
        disabled={!hasActiveFilters}
        onClick={handleReset}
        level="tableControls"
        height="var(--table-controls-height)"
        color={button.primaryText}
        bgColor={button.primaryBg}
        label="Reset"
      />
      <AppButton
        overrideHover
        onClick={() => setShowCreate(true)}
        level="tableControls"
        height="var(--table-controls-height)"
        color={button.primaryText}
        bgColor={page.highlight}
        label="+ New Audits"
      />
    </Box>
  );

  const renderContents = () => {
    if (loading || !audits || audits.length === 0) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          showRefreshButton={!loading && Boolean(hasActiveFilters)}
          refreshQuery={handleReset}
          buttonHighlight={button.primaryBg}
          loading={loading || !audits}
          label="Loading audits…"
          noDataLabel="No audits found. Try adjusting your filters."
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }

    return (
      <Box fill="horizontal" direction="column">
        {!small && (
          <Box
            fill="horizontal"
            wrap={false}
            direction="row"
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
          >
            {visibleHeader.map(({ uid, display, size }) => (
              <Box key={uid} width={size} pad={{ horizontal: '0.5rem', top: '1rem', bottom: '0.5rem' }}>
                {renderTableHeader(display, small)}
              </Box>
            ))}
          </Box>
        )}
        {audits.map((row, idx) => (
          <Box
            // eslint-disable-next-line no-underscore-dangle
            key={row.id || row._id}
            direction={small ? 'column' : 'row'}
            align={small ? 'start' : 'center'}
            border={{
              color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
            }}
            pad={{ vertical: '0.75rem', right: '0.5rem' }}
            background={page.tableBg}
            fill="horizontal"
          >
            {visibleHeader.map(({ uid, display, size }) => (
              <Box
                key={uid}
                width={small ? '100%' : size}
                pad={{ horizontal: small ? '0' : '0.5rem', vertical: small ? '0.25rem' : '0' }}
                direction="row"
                gap="0.5rem"
              >
                {small && display && (
                  <Text size="0.875rem" color={page.tableHeader}>
                    {display}
                    :
                  </Text>
                )}
                {renderCell(uid, row, idx)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'YT Audits',
        subTitle: 'Content ID · Admin',
      }}
    >
      <Seo />
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '1.5rem' }}
        height="100%"
        gap="1rem"
        width="100%"
      >
        <InfoNotice
          header="YouTube audits"
          text="Submit and track YouTube channel audits. Completed audits show infringing channels, reuploads, and estimated revenue lost — visible to admins only."
        />
        <Box pad={{ horizontal: padHorizontal }} width="100%">
          <TableWrapper
            zeroBasedPageIndex
            small={small}
            header="Audits"
            boxPad={tablePadding(small)}
            isPaged={total > PAGE_SIZE}
            totalResults={total}
            currentResults={(audits || []).length}
            selectedResultsCount={PAGE_SIZE}
            currentPage={currentPage}
            handlePage={(p) => setCurrentPage(p)}
            overrideNextPageDisabled={currentPage * PAGE_SIZE + PAGE_SIZE >= total}
            tableControls={tableControls}
          >
            <Box fill="horizontal" direction="column">
              {renderContents()}
            </Box>
          </TableWrapper>
        </Box>
      </Box>

      {showCreate && (
        <AdminYtAuditsCreateModal
          small={small}
          onClose={() => setShowCreate(false)}
          onSubmit={handleBatchCreate}
          submitting={submitting}
        />
      )}

      {showDetail && detail && (
        <AdminYtAuditDetailModal
          small={small}
          audit={detail}
          onClose={handleCloseDetail}
          onRemoveChannel={removeChannel}
        />
      )}

      {showDatePicker && (
        <Layer
          responsive={false}
          animate={false}
          onEsc={handleCloseDatePicker}
          onClickOutside={handleCloseDatePicker}
        >
          <DatetimeCalendar
            hideTimeZone
            showCloseButton
            showTimeSelect={false}
            handleClose={handleCloseDatePicker}
            small={small}
            calendarMode="range"
            setDateRange={handleDateRangeSelect}
            dateRangeValue={dateRangeValue}
          />
        </Layer>
      )}
    </AdminNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    loading: state.fetchLoader.dataLoading,
    audits: state.adminYtAudits.audits,
    total: state.adminYtAudits.total,
    detail: state.adminYtAudits.detail,
    adminUsers: state.adminYtAudits.adminUsers,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchAudits: fetchAdminYtAudits,
    fetchDetail: fetchAdminYtAuditDetail,
    fetchUsers: fetchAdminYtAuditUsers,
    batchCreate: batchCreateAdminAudits,
    removeChannel: removeAdminYtAuditChannel,
    clearDetail: adminYtAuditDetailClear,
    clearAudits: adminYtAuditsClear,
  }, dispatch);
}

AdminYtAuditsPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  audits: PropTypes.array,
  total: PropTypes.number,
  detail: PropTypes.object,
  adminUsers: PropTypes.array,
  fetchAudits: PropTypes.func.isRequired,
  fetchDetail: PropTypes.func.isRequired,
  fetchUsers: PropTypes.func.isRequired,
  batchCreate: PropTypes.func.isRequired,
  removeChannel: PropTypes.func.isRequired,
  clearDetail: PropTypes.func.isRequired,
  clearAudits: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminYtAuditsPage);
